import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Award, Loader2, RotateCcw, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Admin-only card that lets the admin reassign the bonus credit of a
 * won lead to another operator. Overrides `first_contact_by_id` in the
 * office bonus calculation. Persists via PATCH /api/leads/{id}/bonus-credit.
 *
 * Props:
 *   lead        — current lead object
 *   currentUser — logged-in user
 *   onChanged   — called after a successful patch (parent reloads lead)
 */
export default function BonusCreditBlock({ lead, currentUser, onChanged }) {
  const isAdminOrDirecao = ["admin", "direcao"].includes(currentUser?.role);
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const credit = lead?.bonus_credit || null;
  const currentCreditName = credit?.user_name || null;
  const firstContactName = lead?.first_contact_by_name || null;

  // Load users when the block expands (deferred fetch — avoids network on
  // every LeadDetail render for non-admins).
  useEffect(() => {
    if (!expanded || users.length) return;
    setLoadingUsers(true);
    api.get("/users")
      .then((r) => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Não foi possível carregar utilizadores"))
      .finally(() => setLoadingUsers(false));
  }, [expanded, users.length]);

  const eligible = useMemo(() => {
    // Regra da direcção: só operadores de atendimento podem receber
    // o mérito do bónus. O backend também rejeita outros roles (400).
    const active = users.filter(
      (u) => u.active !== false && u.role === "atendimento",
    );
    return [...active].sort((a, b) =>
      (a.name || a.email || "").localeCompare(b.name || b.email || "")
    );
  }, [users]);

  if (!isAdminOrDirecao) return null;

  const save = async (clear = false) => {
    if (!clear && !targetId) {
      toast.error("Escolhe um utilizador para atribuir o mérito");
      return;
    }
    setSaving(true);
    try {
      const body = clear
        ? { user_id: null, reason: reason || null }
        : { user_id: targetId, reason: reason || null };
      const r = await api.patch(`/leads/${lead.id}/bonus-credit`, body);
      toast.success(
        clear
          ? "Sobreposição removida — o mérito volta ao primeiro contacto"
          : `Mérito atribuído a ${r.data?.bonus_credit?.user_name || "operador"}`
      );
      setTargetId("");
      setReason("");
      setExpanded(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a gravar reatribuição");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3"
      data-testid="bonus-credit-block"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
          <Award className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">
            Mérito do lead (bónus)
          </div>
          <div className="text-sm text-foreground mt-0.5">
            {credit ? (
              <>
                Atribuído manualmente a <b>{currentCreditName}</b>
                {credit.reason && (
                  <span className="text-muted-foreground"> — {credit.reason}</span>
                )}
              </>
            ) : (
              <>
                A ser atribuído ao primeiro contacto:{" "}
                <b>{firstContactName || "(sem primeiro contacto)"}</b>
              </>
            )}
          </div>
          {credit && (
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Alterado por {credit.changed_by_name || credit.changed_by}
              {credit.changed_at && ` · ${new Date(credit.changed_at).toLocaleString("pt-PT")}`}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted/40 whitespace-nowrap"
          data-testid="bonus-credit-toggle"
        >
          {expanded ? "Fechar" : credit ? "Editar" : "Reatribuir"}
        </button>
      </div>

      {expanded && (
        <div className="pt-2 border-t border-amber-500/25 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">
              Atribuir mérito a
            </label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              disabled={loadingUsers || saving}
              className="mt-1 w-full px-3 py-2 rounded-lg text-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-foreground/20 disabled:opacity-50"
              data-testid="bonus-credit-user-select"
            >
              <option value="">— Selecionar utilizador de atendimento —</option>
              {eligible.map((u) => (
                <option key={u.id} value={u.id}>
                  {(u.name || u.email || u.id)}
                  {u.email ? ` · ${u.email}` : ""}
                </option>
              ))}
            </select>
            {!loadingUsers && eligible.length === 0 && (
              <div className="mt-1 text-[11px] text-muted-foreground">
                Nenhum utilizador com role <code>atendimento</code> ativo encontrado.
              </div>
            )}
            {loadingUsers && (
              <div className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> A carregar utilizadores…
              </div>
            )}
            <p className="mt-1 text-[11px] text-muted-foreground italic">
              Só operadores de atendimento podem receber o mérito do bónus.
            </p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">
              Motivo (auditoria)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: primeiro contacto foi feito pelo João mas ficou registado noutro"
              disabled={saving}
              className="mt-1 w-full px-3 py-2 rounded-lg text-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-foreground/20 disabled:opacity-50"
              data-testid="bonus-credit-reason"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => save(false)}
              disabled={saving || !targetId}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50"
              data-testid="bonus-credit-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
              Gravar mérito
            </button>
            {credit && (
              <button
                type="button"
                onClick={() => save(true)}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-border bg-card hover:bg-muted/40 disabled:opacity-50"
                data-testid="bonus-credit-clear-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Remover sobreposição
              </button>
            )}
            <button
              type="button"
              onClick={() => setExpanded(false)}
              disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-muted/40"
              data-testid="bonus-credit-cancel-btn"
            >
              <X className="w-3.5 h-3.5" /> Cancelar
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground italic">
            Esta reatribuição sobrepõe o primeiro contacto no cálculo do bónus e é
            registada no histórico do lead. Só admin/direcao pode alterar.
          </p>
        </div>
      )}
    </div>
  );
}
